<script setup>
  import { ref, watchEffect, onMounted } from 'vue';
  import { useApiWindAlerts } from '@/composables/useApiWindAlerts';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';
  import BaseModal from './base/BaseModal.vue';
  import Toggle from './controls/Toggle.vue';
  import WindAlertHistoryList from './WindAlertHistoryList.vue';
  import LoaderCircle from './LoaderCircle.vue';
  import { normalizeAlertRecord } from '@/helpers/alertHistory';

  const apiWindAlerts = useApiWindAlerts();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const emits = defineEmits(['close']);

  const props = defineProps({
    show: {
      type: Boolean,
      default: false
    }
  });

  const showModal = ref(false);
  const isLoading = ref(false);
  const error = ref(null);
  const alerts = ref([]);
  const groupBySpot = ref(false);

  async function loadHistory() {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiWindAlerts.getAlertHistory();
      const data = response?.data;
      const reqError = response?.error;
      if (!data || reqError?.value) {
        error.value = reqError?.value || new Error('Failed to load alert history');
        alerts.value = [];
      } else {
        const value = data.value;
        const records = Array.isArray(value)
          ? value
          : value?.alert_records || value?.alerts || value?.alert_history || [];
        alerts.value = records.map(normalizeAlertRecord);
      }
    } catch (e) {
      error.value = e;
      alerts.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  function closeModal() {
    showModal.value = false;
    emits('close');
  }

  const handleGroupBySpotChange = (groupBySpotOn) => {
    googleAnalyticsStore.sendEvent(groupBySpotOn ? 'Alerts_Group_By_Spot_On' : 'Alerts_Group_By_Spot_Off');
  }

  onMounted(() => {
    watchEffect(() => {
      showModal.value = props.show;
      if (props.show) {
        groupBySpot.value = false;
        loadHistory();
      }
    });
  });
</script>

<template>
  <BaseModal
    v-model="showModal"
    :allowOutsideClick="true"
    height="tall"
    @close="closeModal"
  >
    <header class="jwx-modal__header">
      <h2 class="jwx-modal__title">Alert History</h2>
    </header>

    <div class="jwx-modal__body">
      <div class="jwx-modal__section">
        <div class="jwx-modal__section-body">
          <ul class="jwx-block jwx-table-view" data-surface="1">
            <li class="jwx-table-view__row">
              <div class="jwx-table-view__row-content">
                <label for="alert-history-group-by-spot" class="jwx-table-view__row-label jwx-font-weight-400">Group by Spot</label>
                <div class="jwx-table-view__row-actions">
                  <Toggle
                    v-model="groupBySpot"
                    @input:change="handleGroupBySpotChange"
                    name="alert-history-group-by-spot"
                    id="alert-history-group-by-spot"
                  />
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="isLoading" class="jwx-grid jwx-place-content-center jwx-p-jumbo">
        <LoaderCircle :showText="false" />
      </div>

      <div v-else-if="error" class="jwx-modal__section">
        <div class="jwx-modal__section-body jwx-text-center">
          <p class="jwx-text jwx-m-bottom-lg">Unable to load alert history.</p>
          <button type="button" class="jwx-button jwx-button--pill" @click="loadHistory">Retry</button>
        </div>
      </div>

      <div v-else-if="alerts.length === 0" class="jwx-modal__section">
        <div class="jwx-modal__section-body">
          <p class="jwx-text jwx-text-center jwx-p-xxl">No recent alerts.</p>
        </div>
      </div>

      <WindAlertHistoryList
        v-else
        :alerts="alerts"
        :groupBySpot="groupBySpot"
        :showSpotName="true"
      />
    </div>
  </BaseModal>
</template>
